/**
 * auditAccess — who may open the Audit Log.
 *
 * The log is a record of when every signed-in person was using the app and for
 * how long. That is monitoring data about colleagues, so it is default-DENY,
 * the way HRIS and Subcontractors are: an address on the list may open it and
 * nobody else, and the sidebar does not even show the entry to anyone else.
 *
 * Mikko asked for the screen on 2026-10-09 and is the only account named, so
 * the default list is his two sign-in addresses. Adding somebody is a code
 * change here or, without one, the environment variable below.
 *
 * ── THIS GATES THE APP UI ONLY ────────────────────────────────────────────
 * Anyone whose account can read the `iSyn_AuditLog` list in SharePoint can
 * still read it through the SharePoint API or the list view, with or without
 * this app. To make it real, restrict that list's permissions in SharePoint to
 * the same people. This stops a screen opening; it does not stop a read.
 *
 * Matched on the WHOLE sign-in address and nothing else — never the display
 * name, which is whatever the directory says it is.
 *
 *   VITE_AUDIT_LOG_USERS   comma-separated, replaces the default list
 */
import { useMemo } from "react";
import { useMsal } from "@azure/msal-react";

const lc = (s?: string | null): string => (s ?? "").trim().toLowerCase();

export const AUDIT_LOG_USERS: readonly string[] = (() => {
  const env = import.meta.env?.VITE_AUDIT_LOG_USERS as string | undefined;
  if (env) return env.split(",").map(lc).filter(Boolean);
  return [
    // Mikko — the tenant account and the address he signs in with.
    "mikko@intalgarmentsopc.onmicrosoft.com",
    "intalmiks11@gmail.com",
  ];
})();

/**
 * May this account open the Audit Log? Pure, so the rule can be tested without a
 * signed-in browser. No address — what a half-initialised MSAL cache looks like —
 * is refused: absence of evidence is not evidence of access.
 */
export function canOpenAuditLog(username?: string | null): boolean {
  const email = lc(username);
  return !!email && AUDIT_LOG_USERS.includes(email);
}

/** The same answer, for a component. */
export function useAuditLogAccess(): boolean {
  const { accounts } = useMsal();
  const username = accounts[0]?.username;
  return useMemo(() => canOpenAuditLog(username), [username]);
}
